import { EntityType } from "Model/EntityType";
import { IUser } from "Model/Storage/IUser";
import { hasNoElements } from "./Array";
import { Configuration } from "./Configuration";
import { dateFromString, isDate } from "./Date";
import { isNotFunction } from "./Function";
import { getDateTimeFormatter } from "./Intl";
import { isAnyDateField, isStringField } from "./Metadata";
import { isNullish } from "./Null";
import { isNumber } from "./Number";
import { isObject } from "./Object";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace } from "./String";
import { toTokenSets } from "./Tokenizr";

let knownUsernames: string[] = [];

export async function filterByExpression<T>(
  items: T[],
  key: string,
  expression: string,
  usernames: string[]
): Promise<string[]> {
  await Configuration.getInstanceAsync();

  const expressionSets = toTokenSets(expression);
  if (hasNoElements(expressionSets)) {
    return items.map((i) => i[key]);
  }

  knownUsernames = usernames;
  return expressionSets
    .reduce((output, [fieldName, search]) => filterBy(output, fieldName, search), items)
    .map((i) => i[key]);
}

function filterBy<T>(output: T[], fieldName: string, search: string): T[] {
  const fieldNames = fieldName === `*` ? Configuration.getFilterFieldNames() : [fieldName];
  return output.filter((item) => itemContainsSomeValue(item, fieldNames, search));
}

function itemContainsSomeValue<T>(item: T, fieldNames: string[], search: string): boolean {
  return fieldNames.some((fieldName) => itemContainsValue(item, fieldName, search));
}

function itemContainsValue<T>(item: T, fieldName: string, search: string): boolean {
  if (isOperator(search)) {
    return evaluateOperator(item, fieldName, search);
  }

  const data = item[fieldName];
  if (containsOperator(search)) {
    if (isNumber(data)) {
      return evaluateNumericOperator(data, search);
    }

    const meta = Configuration.getMetadata(fieldName, EntityType.Project);
    if (isAnyDateField(meta)) {
      return evaluateDateOperator(data, search);
    }

    if (isStringField(meta) && search.startsWith(`=`)) {
      return data === search.slice(1);
    }
  }

  const value = toNormalizedString(data);
  return value.includes(toNormalizedString(search));
}

// longest operators first
const operatorMap = {
  ">=": (a: number, b: number) => a >= b,
  "<=": (a: number, b: number) => a <= b,
  ">": (a: number, b: number) => a > b,
  "<": (a: number, b: number) => a < b,
  "=": (a: number, b: number) => a === b
};

export function containsOperator(search: string): boolean {
  if (isNullOrWhiteSpace(search)) {
    return false;
  }
  return Object.keys(operatorMap).some((operator) => search.startsWith(operator));
}

function evaluateNumericOperator(data: number, search: string): boolean {
  const evaluateData = data ?? 0;
  const [operator, searchValue] = parseOperatorAndValue(search);
  const operatorFn: (a: number, b: number) => boolean = operatorMap[operator];
  return operatorFn(evaluateData, parseFloat(searchValue));
}

function evaluateDateOperator(data: string, search: string): boolean {
  const date = isNotNullOrWhiteSpace(data) ? dateFromString(data) : new Date(9000, 0, 1);

  const [operator, searchValue] = parseOperatorAndValue(search);
  const searchDate = searchValue === `@today@` ? today(operator) : dateFromString(searchValue);

  const operatorFn: (a: number, b: number) => boolean = operatorMap[operator];
  return operatorFn(date.valueOf(), searchDate.valueOf());
}

function today(operator: string): Date {
  const now = new Date();

  if (operator.includes(`<`)) {
    now.setHours(0, 0, 0, 0);
  } else if (operator.includes(`>`)) {
    now.setHours(23, 59, 59, 999);
  }

  return now;
}

export function parseOperatorAndValue(search: string): [string, string] {
  const operator = Object.keys(operatorMap).find((op) => search.startsWith(op));
  return [operator, search.replace(operator, ``)];
}

function toNormalizedString(value: unknown): string {
  try {
    if (value == null) {
      return ``;
    }

    if (isObject(value)) {
      return Object.values(value)
        .map((v) => toNormalizedString(v))
        .join(` `);
    }

    if (isDate(value)) {
      return getDateTimeFormatter(`short`, undefined).format(value);
    }

    if (Array.isArray(value)) {
      return value.map((v) => toNormalizedString(v)).join(` `);
    }

    return value.toString().toLocaleLowerCase();
  } catch {
    console.warn(`[Filter]: Could not convert value to string.`, value);
    /* ignore */
  }

  return ``;
}

export function isOperator(search: string): boolean {
  return search.startsWith(`@`) && search.endsWith(`@`);
}

type OperatorFunction = <T>(item: T, fieldName: string) => boolean;

function isMe<T>(item: T, fieldName: string): boolean {
  if (item == null) {
    return false;
  }

  const userList = item[fieldName] as IUser[];

  if (hasNoElements(userList)) {
    return false;
  }

  return userList.some(({ name }) => {
    if (isNullOrWhiteSpace(name)) {
      return false;
    }

    return knownUsernames.map((n) => n.toLowerCase()).includes(name.toLowerCase());
  });
}

function isNull<T>(item: T, fieldName: string): boolean {
  const value = item?.[fieldName];
  if (Array.isArray(value)) {
    return hasNoElements(value);
  }
  return isNullish(value);
}

const OPERATOR_MAP = new Map<string, OperatorFunction>([
  [`@me@`, isMe],
  [`@null@`, isNull],
  [`@notnull@`, (...args) => !isNull(...args)]
]);

function evaluateOperator<T>(item: T, fieldName: string, operator: string): boolean {
  const operatorFn = OPERATOR_MAP.get(operator);
  if (isNotFunction(operatorFn)) {
    console.warn(`[Filtering] No operator function found!`);
    return false;
  }

  return operatorFn(item, fieldName);
}
